import { getSEOTitle } from "@portal/app/_common/lib/metaTitle";
import { Metadata, Viewport } from "next";
import { Course, School, WithContext } from "schema-dts";
import type { StaticImageData } from "next/image";
import { isProduction } from "@portal/app/_common/lib/edgeUtils";

const DEFAULT_DESCRIPTION = "Dreambound creates upward mobility for America.";
const DEFAULT_OPEN_GRAPH_IMAGE = "/images/dreambound_opengraph_landscape.png";

export type SchoolStructuredData = WithContext<School> & {
  name?: string;
};
export type ProgramStructuredData = WithContext<Course> & {
  name?: string;
};

export type SEOContentProps = {
  title?: string;
  /**
   * Appends `| Dreambound` to the title. Off for blog posts and landing pages,
   * whose CMS titles are already self-sufficient and lose SERP width to a
   * brand string the URL already carries. Defaults to true.
   */
  brandSuffix?: boolean;
  image?: string | StaticImageData;
  description?: string;
  canonicalHref?: string;
  noindex?: boolean;
  /**
   * Emits `nofollow` independently of `noindex`. Keep them separate: a page can
   * be excluded from the index while its outbound links stay crawlable — e.g.
   * DreamSearch filter URLs, which are near-duplicates we don't want indexed but
   * which still need to pass crawlers through to school and program pages.
   */
  nofollow?: boolean;
  /**
   * URL (path or absolute; resolved against `metadataBase`) emitted as
   * `og:url` — the identity Meta's object cache keys share counts and crawl
   * dedup on. Defaults to `canonicalHref`, so every page with a canonical gets
   * a dedup identity for free; pass this only when the two must differ.
   * Deliberately independent of the canonical's indexing rules: tracking-
   * tagged URLs are noindexed and emit no canonical on purpose (a canonical
   * alongside noindex risks deindexing its target — see
   * `generateDreamSearchMetadata`), which left crawlers with no dedup signal
   * at all, so Meta crawled every tracking-param variant of one ad as a
   * distinct object (ENG-8379). `og:url` carries no indexing semantics, so it
   * is safe to emit on noindexed URLs.
   */
  ogUrl?: string;
  article?: {
    publishedTime: string;
    modifiedTime: string;
    author?: string;
    section?: string;
  };
};

export function generateSEOMetadata({
  title: rawTitle,
  brandSuffix = true,
  image = DEFAULT_OPEN_GRAPH_IMAGE,
  description = DEFAULT_DESCRIPTION,
  canonicalHref,
  noindex,
  nofollow,
  ogUrl,
  article,
}: SEOContentProps = {}): Metadata {
  const imagePath = typeof image === "string" ? image : image?.src;
  const fullImagePath = imagePath
    ? process.env.DREAM_PORTAL_URL && !imagePath.startsWith("http")
      ? `${process.env.DREAM_PORTAL_URL}${imagePath}`
      : imagePath
    : undefined;

  const title = getSEOTitle(rawTitle, brandSuffix);
  const openGraphUrl = ogUrl ?? canonicalHref;

  return {
    title,
    description,
    alternates: {
      canonical: canonicalHref,
    },
    robots: {
      index: !noindex && isProduction(),
      follow: !nofollow && isProduction(),
    },
    openGraph: article
      ? {
          title,
          description,
          type: "article",
          publishedTime: article.publishedTime,
          modifiedTime: article.modifiedTime,
          ...(article.author && { authors: [article.author] }),
          ...(article.section && { section: article.section }),
          ...(fullImagePath && { images: fullImagePath }),
          ...(openGraphUrl && { url: openGraphUrl }),
        }
      : {
          title,
          description,
          type: "website",
          ...(fullImagePath && { images: fullImagePath }),
          ...(openGraphUrl && { url: openGraphUrl }),
        },
    twitter: {
      card: fullImagePath ? "summary_large_image" : "summary",
      creator: "@dreambound",
      title,
      description,
      ...(fullImagePath && { images: fullImagePath }),
    },
    metadataBase: new URL(process.env.DREAM_PORTAL_URL || "https://www.dreambound.com"),
  };
}

export const defaultViewport: Viewport = {
  width: "device-width",
  initialScale: 1,
};
